import * as React from "react";
import { RouteComponentProps } from "@reach/router";
import { FormikProps } from "formik";
import { cx, css } from "@emotion/css";
import SigPad from "signature_pad";
import lodash from "lodash";
import { FormValues } from "../support/FormValues";
import bootstrapStyles from "../css/bootstrap.module.css";
import Breadcrumbs from "../components/Breadcrumbs";
import { ClassName } from "../support/className";
import { StepHeader } from "../components/StepHeader";
import { GeneralButton } from "../components/GeneralButton";
import { FieldNameEnum } from "../support/FieldNameEnum";
import { jsLogger } from "../libs/jsLogger";
import { PfSignaturePad } from "../components/PfSignaturePad";
import { SubmitButton } from "../components/SubmitButton";
import { navigate } from "../support/hashRouting";
import { useScrollTop } from "../customHooks/scrollTop";

interface SignProps extends RouteComponentProps {
  formikBag: FormikProps<FormValues>;
  location?: any;
}

const stepHeaderText = "You're almost done.";
const stepNoteText =
  "You must scroll and review all of the following disclosures before proceeding.";

const disclsoureBullets = css`
  margin-top: 32px;
  li {
    padding: 5px 15px;
  }
`;

const Sign = ({
  formikBag: {
    values,
    errors,
    touched,
    setFieldValue,
    isSubmitting,
    validateForm,
    submitForm
  },
  location
}: SignProps) => {
  useScrollTop(location);

  const sigCanvas = React.useRef<SigPad | null>(null);
  const [pageSigned, setPageSigned] = React.useState(false);
  const [signRequired, setSignRequired] = React.useState(false);

  const clearSignature = () => {
    setFieldValue(FieldNameEnum.signature, "");
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    setPageSigned(false);
    setSignRequired(false);
  };

  const saveSignature = () => {
    const signatureData = sigCanvas.current
      ? sigCanvas.current.toDataURL("image/svg+xml")
      : "";
    if (signatureData.length < 2200) {
      setSignRequired(true);
      return;
    }
    setFieldValue(FieldNameEnum.signature, signatureData);
    setPageSigned(true);
    setSignRequired(false);
  };

  const submitErrors = () => {
    const obj = {
      errors,
      values,
      touched
    };
    jsLogger.log(JSON.stringify(obj), "Rtt - Sign Page");
  };

  return (
    <div className={ClassName.signWrapper}>
      <Breadcrumbs currentStep={4} />
      <StepHeader stepHeader={stepHeaderText} stepNote={stepNoteText} />

      <div className={cx(bootstrapStyles.row, disclsoureBullets)}>
        <div className={bootstrapStyles["col-md-12"]}>
          <ul className="">
            <li>
              I have read the explanation below regarding the consequences of
              commingling rollover assets with other IRA assets. I understand
              that I am authorizing commingling to occur.
            </li>
            <li>
              Commingling Contributions: Funds rolled over from an employer’s
              retirement plan (“rollover assets”) can be kept in a separate
              account, usually in a Rollover IRA. This allows you the option to
              return the rollover assets to a qualified retirement plan in the
              future. Commingling rollover and other IRAs together may cause you
              to forfeit that option in the future. Additionally, a Rollover IRA
              allows you to maintain eligibility for special tax treatment under
              an income-averaging or capital gains elections for qualified plan
              distributions. If you wish to preserve these options and special
              tax treatment, when applicable, please contact us.
            </li>
            <li>
              After two years, SIMPLE IRA assets may be transferred or rolled
              over to other plan types if authorized by the plan.
            </li>

            <li>
              SIMPLE IRA to SIMPLE IRA transfers may be made at any time.
              Custodian transfers from other types of IRAs (except Roth IRAs)
              and rollovers from 403(b)s, 457(b)s and other qualified retirement
              plans may only be made after two years of participation in the
              SIMPLE IRA. The two-year participation period begins on the date
              the first contribution is made to any SIMPLE IRA plan maintained
              by your employer. If you initiate a transfer/rollover of
              non-SIMPLE IRA assets to a SIMPLE IRA and the two-year
              participation rule has not been met, we will place those assets in
              a Traditional IRA.
            </li>
            <li>
              If you made after-tax contributions to your plan, you must tell us
              the amount of these contributions. Otherwise, rollover proceeds
              are assumed to be pre-tax contributions and earnings unless your
              current custodian indicates otherwise in the rollover
              documentation.
            </li>
            <li>
              Roth assets held in a Roth IRA may be transferred only to a Roth
              IRA. Roth assets held in a qualified retirement plan may be moved
              either to a Roth IRA or a 401(k), 403(b) or 457(b) that accepts
              Roth contributions.
            </li>
            <li>
              I have received and read the Disclosure Statement and Custodial
              Agreement for my plan, if applicable.
            </li>
            <li>
              I am of legal age, or I am accepting the appointment of
              Responsible Individual for the IRA that is being established for
              an individual who is a minor.
            </li>
            <li>
              I have read and agree to be bound by the provisions of the
              prospectus for the fund(s) in which I am investing.
            </li>
            <li>
              I have read and understand the{" "}
              <a
                href="https://res.americancentury.com/docs/service_options"
                target="_blank"
                rel="noreferrer"
              >
                Service Options
              </a>{" "}
              flyer, which is part of this application by reference.
            </li>

            <li>
              I understand that providing my email address gives American
              Century Investments permission to send me information about
              products and services via email.
            </li>
            <li>
              I authorize American Century Services, LLC (‘‘American Century’’),
              its affiliated companies and agents, to act upon my instructions
              provided herein. I understand that I am authorized to transact
              business on this account by telephone, online, by fax, in writing,
              or by any other means acceptable to American Century. This
              authorization applies to all like registered current and future
              accounts in all investment companies in the American Century
              family, listed under the taxpayer identification number shown on
              this form.
            </li>
            <li>
              In consideration of American Century accepting this account
              application, I agree to defend, hold harmless and indemnify
              American Century and its officers, agents, employees, affiliates
              and successors from liability for any loss, claim or expense that
              I may sustain as a result of their acting on transaction
              instructions they believe to be genuine.
            </li>
            <li>
              I understand that American Century will use reasonable procedures
              to confirm that instructions submitted by any account owner or
              trustee online, by telephone, by fax, in writing, or by any other
              means acceptable to American Century, are genuine, including
              personal identification, recording of telephone conversations and
              providing written or electronic confirmation of each transaction.
              A failure on their part to employ such procedures may subject them
              to liability for any loss due to unauthorized or fraudulent
              instructions.
            </li>
            <li>
              I understand that neither American Century nor its affiliated
              companies or agents shall be responsible or liable for any damages
              related to online services including but not limited to those
              caused by theft, unauthorized access, failure of electronic or
              mechanical equipment, communications line failure or telephone or
              interconnectivity problems or other occurrences beyond their
              control.
            </li>
            <li>
              If I am making a rollover deposit to a plan, I hereby certify that
              the funds are eligible for rollover and I irrevocably elect to
              treat the distribution I received from my prior plan as a rollover
              contribution.
            </li>
            <li>
              I acknowledge that State Street Bank and Trust Company and
              American Century shall not be liable for any tax or other
              consequences in connection with contributions to my IRA.
            </li>
            <li>
              I authorize my current provider to release account information to
              American Century Investments.
            </li>
            <li>
              Important Information About New Accounts: A federal law,
              established to help stop the funding of terrorism and money
              laundering activities, requires financial institutions to verify
              the identity of each person who opens an account. American Century
              will verify your identity using the name, street address, date of
              birth and Social Security number that you provide in this
              application. In some instances, we may request additional
              documentation.
            </li>
            <li>
              Custodial Acceptance: If all required forms and information are
              properly submitted, State Street Bank and Trust Company will
              accept appointment as Custodian of the Custodial Account. However,
              this Agreement is not binding upon the Custodian until the
              Depositor has received a statement confirming the initial
              transaction for the Custodial Account. Receipt by the Depositor of
              a confirmation of the purchase of the Fund shares indicated in the
              Depositor’s Application will serve as notification of State Street
              Bank and Trust Company’s acceptance of appointment as Custodian of
              the Custodial Account.
            </li>
          </ul>
        </div>
      </div>
      <div className={bootstrapStyles.row}>
        <div className={bootstrapStyles["col-md-12"]}>
          <div>
            <PfSignaturePad
              ref={sigCanvas}
              redrawOnResize
              aspect={0.3}
              options={{ penColor: "#0069a6" }}
            />
          </div>
        </div>
        {signRequired && (
          <div
            className={css`
              margin-bottom: 10px;
              margin-left: 20px;
              color: #eb3c36;
            `}
          >
            This field is required.
          </div>
        )}
      </div>

      <div className={bootstrapStyles["col-md-12"]}>
        <GeneralButton
          id="clearSignature"
          buttonType="secondary"
          text="Clear Signature"
          onClick={clearSignature}
        />
        <div
          className={css`
            float: right;
          `}
        >
          <GeneralButton
            id="acceptSignature"
            buttonType="primary"
            text="Accept Signature"
            onClick={() => {
              saveSignature();
            }}
          />
        </div>
      </div>
      <div className={bootstrapStyles.row}>
        <div className={bootstrapStyles["col-md-12"]}>
          <hr />
        </div>
      </div>

      <div className={bootstrapStyles.row}>
        <div className={cx(bootstrapStyles["col-md-12"])}>
          <GeneralButton
            id="back"
            text="back"
            buttonType="secondary"
            onClick={() => {
              navigate("/review");
            }}
          />
          <div
            className={css`
              float: right;
            `}
          >
            <SubmitButton
              isSubmitting={isSubmitting}
              disabled={!pageSigned || isSubmitting}
              onClick={e => {
                e.preventDefault();
                validateForm().then(() => {
                  if (lodash.isEmpty(errors)) {
                    submitForm();
                  } else {
                    submitErrors();
                  }
                });
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sign;
